import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import ReactApexChart from "react-apexcharts";

function EmailResults() {
  const location = useLocation();
  const { campaignId, campaignName } = location.state || {};

  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!campaignId) {
      setError("No campaign ID provided.");
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_SES_INSIGHTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ campaignId }),
        });

        if (!response.ok) throw new Error("Failed to fetch stats.");

        const result = await response.json();
        const data = JSON.parse(result.body);

        const sends = parseMetric(data.sends);
        const deliveries = parseMetric(data.deliveries);
        const bounces = parseMetric(data.bounces);
        const opens = parseMetric(data.opens);
        const spam = parseMetric(data.spam);
        const complaint = parseMetric(data.complaint);
        const deliveryDelays = parseMetric(data.deliveryDelays);

        setStats({
          sends,
          deliveries,
          bounces,
          opens,
          spam,
          complaint,
          deliveryDelays,
        });
      } catch (err) {
        setError(err.message);
      }
    };

    fetchStats();
  }, [campaignId]);

  if (error) return <div>Error: {error}</div>;
  if (!stats) return <div>Loading stats...</div>;

  // Calculate rates: bounceRate now uses sends as denominator
  const openRate = stats.deliveries ? (stats.opens / stats.deliveries) * 100 : 0;
  const bounceRate = stats.sends ? (stats.bounces / stats.sends) * 100 : 0;
  const deliveryRate = stats.sends ? (stats.deliveries / stats.sends) * 100 : 0;

  const radialBarSeries = [
    parseFloat(openRate.toFixed(1)),
    parseFloat(bounceRate.toFixed(1)),
    parseFloat(deliveryRate.toFixed(1)),
  ];

  const radialBarOptions = {
    chart: { type: "radialBar" },
    labels: ["Open Rate", "Bounce Rate", "Delivery Rate"],
    plotOptions: {
      radialBar: {
        hollow: { size: "64%" },
        dataLabels: {
          name: { fontSize: "14px", offsetY: 30 },
          value: { fontSize: "20px", offsetY: -10, formatter: (val) => `${val}%` },
        },
      },
    },
    stroke: { lineCap: "round" },
    legend: { position: "bottom" },
    tooltip: { enabled: false },
  };

  return (
    <Layout>
      <div className="py-12 bg-gray-50 sm:py-16 lg:py-20">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Campaign Stats for {campaignName || "Unknown Campaign"}
          </h1>

          {/* Rates Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 px-16 py-12 bg-white rounded-2xl text-center mb-12">
            <RateTile label="Open Rate" value={openRate} />
            <RateTile label="Bounce Rate" value={bounceRate} />
            <RateTile label="Delivery Rate" value={deliveryRate} />
          </div>

          {/* Radial Chart */}
          <div className="mb-12">
            <ReactApexChart
              options={radialBarOptions}
              series={radialBarSeries}
              type="radialBar"
              height={480}
            />
          </div>

          {/* Detailed Stats with Icons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-6">
            <StatCard icon={<ArrowRightIcon />} label="Sends" value={stats.sends} iconBgClass="bg-purple-500 text-white" />
            <StatCard icon={<EnvelopeIcon />} label="Deliveries" value={stats.deliveries} iconBgClass="bg-blue-500 text-white" />
            <StatCard icon={<XCircleIcon />} label="Bounces" value={stats.bounces} iconBgClass="bg-red-500 text-white" />
            <StatCard icon={<EyeIcon />} label="Opens" value={stats.opens} iconBgClass="bg-green-500 text-white" />
            <StatCard icon={<ShieldCheckIcon />} label="Spam" value={stats.spam} iconBgClass="bg-yellow-500 text-white" />
            <StatCard icon={<ExclamationIcon />} label="Complaints" value={stats.complaint} iconBgClass="bg-amber-600 text-white" />
            <StatCard icon={<ClockIcon />} label="Delivery Delays" value={stats.deliveryDelays} iconBgClass="bg-pink-500 text-white" />
          </div>
        </div>
      </div>
    </Layout>
  );
}

// helpers
const parseMetric = (field) => (typeof field === 'object' ? parseInt(field.N, 10) : field || 0);

// Tiles
const RateTile = ({ label, value }) => (
  <div>
    <p className="text-5xl font-bold">{value.toFixed(1)}%</p>
    <h3 className="mt-2 text-sm uppercase text-gray-600">{label}</h3>
  </div>
);

// StatCard accepts iconBgClass
const StatCard = ({ icon, label, value, iconBgClass = "bg-gray-100 text-gray-700" }) => (
  <div className="p-6 bg-white border border-gray-200 rounded-xl flex items-center">
    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${iconBgClass}`}>{icon}</div>
    <div className="ml-4">
      <p className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-sm text-gray-600 uppercase">{label}</p>
    </div>
  </div>
);

// Icons
const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.5 19.5l9-9-9-9m0 18V10.5H3" />
  </svg>
);
const EnvelopeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2.94 6.94a1.5 1.5 0 011.06-.44h11a1.5 1.5 0 011.06.44L10 12.06 2.94 6.94zM5 19h10a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v9a2 2 0 002 2z" />
  </svg>
);
const XCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-10.293a1 1 0 00-1.414-1.414L10 8.586 7.707 6.293a1 1 0 00-1.414 1.414L8.586 10l-2.293 2.293a1 1 0 001.414 1.414L10 11.414l2.293 2.293a1 1 0 001.414-1.414L11.414 10l2.293-2.293z" clipRule="evenodd" />
  </svg>
);
const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 3a7 7 0 00-7 7 7 7 0 0014 0 7 7 0 00-7-7zm0 12a5 5 0 110-10 5 5 0 010 10z" />
    <path d="M10 7a3 3 0 100 6 3 3 0 000-6z" />
  </svg>
);
const ShieldCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m1-4H6a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2z" />
  </svg>
);
const ExclamationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M8.257 3.099c.366-.446.98-.446 1.347 0l6.356 7.756A1 1 0 0115.356 12H4.644a1 1 0 01-.604-1.145l2.618-7.756zm-.894 9.856a1 1 0 011.988 0l.354 1.06a1 1 0 01-.959 1.328h-1.778a1 1 0 01-.959-1.328l.354-1.06z" />
  </svg>
);
const ClockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9 2a7 7 0 00-7 7v5a2 2 0 002 2h7.586c.253 0 .506-.098.697-.293l4.414-4.414A1 1 0 0017 11.586V9a7 7 0 00-7-7H9z" />
  </svg>
);

export default EmailResults;
